/**
 * Login — Soft-launch redesign (Feb 2026)
 *
 * Implements the user-approved mockup that priorities the "existing
 * student migration" funnel ABOVE the standard login form:
 *
 *   1. MESP header (logo + tagline)
 *   2. Card (navy + thin gold border):
 *      a. "هل تدرس معنا حالياً؟" hero block + gold CTA "استكمال الانضمام للمنصة"
 *      b. Divider "أو تسجيل الدخول"
 *      c. Email + Password + inline "نسيت كلمة المرور؟"
 *      d. Gold "تسجيل الدخول" button
 *      e. Divider "أو"
 *      f. White "المتابعة باستخدام Google" button
 *      g. Two side-by-side secondary cards:
 *         • "ليس لديك حساب؟ قدم الآن"
 *         • "تدرس معنا حالياً؟ انضم للمنصة"
 *   3. Language pill (English / العربية) + globe icon
 *   4. Privacy · Terms
 *
 * No new business logic — auth/2FA/Google flow are unchanged. Only the
 * presentation layer was rebuilt.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  LogIn, BookOpen, ArrowLeft, UserCheck, UserPlus, GraduationCap,
  Eye, EyeOff, Mail, Lock, Globe,
} from 'lucide-react';
import { tSafeError } from '../lib/labels';
import { isParentModuleEnabled } from '../lib/featureFlags';
import {
  OnboardingShell, OnboardingHeader, OnboardingCard, GoldButton,
  GoogleSignInButton, OnboardingAlert,
} from '../components/onboarding';
import { startEmergentGoogleAuth } from '../utils/startEmergentGoogleAuth';

// ────────────────────────────────────────────────────────────────
// Local presentation helpers — small enough to stay inline.
// ────────────────────────────────────────────────────────────────

const SecondaryActionCard = ({ to, icon: Icon, question, action, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="flex items-center gap-3 p-3 rounded-xl transition-all flex-1"
    style={{
      background: 'rgba(201, 162, 78, 0.06)',
      border: '1px solid rgba(201, 162, 78, 0.22)',
      textDecoration: 'none',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'rgba(201, 162, 78, 0.12)';
      e.currentTarget.style.borderColor = 'rgba(201, 162, 78, 0.50)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'rgba(201, 162, 78, 0.06)';
      e.currentTarget.style.borderColor = 'rgba(201, 162, 78, 0.22)';
    }}
  >
    <div
      className="shrink-0 flex items-center justify-center"
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'rgba(201, 162, 78, 0.14)',
        border: '1px solid rgba(201, 162, 78, 0.40)',
      }}
    >
      <Icon size={18} color="#C9A24E" strokeWidth={2} />
    </div>
    <div className="min-w-0 flex-1">
      <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.3 }}>
        {question}
      </p>
      <p style={{ fontSize: 13, fontWeight: 700, color: '#C9A24E', marginTop: 2 }}>
        {action}
      </p>
    </div>
  </Link>
);

const SectionDivider = ({ label }) => (
  <div className="my-5 flex items-center gap-3" data-testid="login-divider">
    <div className="flex-1 h-px" style={{ background: 'rgba(201, 162, 78, 0.25)' }} />
    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
      {label}
    </span>
    <div className="flex-1 h-px" style={{ background: 'rgba(201, 162, 78, 0.25)' }} />
  </div>
);

// ────────────────────────────────────────────────────────────────
// Login page
// ────────────────────────────────────────────────────────────────
export const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  // Sep 2026 — Clean Start (auth-loop fix): once auth resolution settles
  // with NO valid user, purge any leftover tokens/hints so a zombie
  // half-session can never auto-redirect back to the dashboard. A user
  // who is genuinely logged in and visits /login is left untouched.
  useEffect(() => {
    if (authLoading || (user && user !== false)) return;
    (async () => {
      try {
        const { clearTokens } = await import('../lib/tokenBridge');
        clearTokens();
      } catch (_e) { /* ignore */ }
      try {
        localStorage.removeItem('mesp_session_hint');
        localStorage.removeItem('mesp_user_snapshot');
      } catch (_e) { /* ignore */ }
      try { document.cookie = 'mesp_session_hint=; Max-Age=0; path=/'; } catch (_e) { /* ignore */ }
    })();
  }, [authLoading, user]);

  // Jun 2026 (prod-health) — a user kicked out mid-session lands here
  // with ?expired=1: show a friendly toast explaining why.
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('expired') !== '1') return;
      window.history.replaceState({}, '', '/login');
      (async () => {
        const { toast } = await import('sonner');
        toast.info(t('Your session has expired. Please sign in again.',
          'انتهت صلاحية جلستك. الرجاء تسجيل الدخول من جديد.'));
      })();
    } catch (_e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Sanitize: strip invisible RTL/zero-width Unicode chars that Arabic
    // keyboards & mobile autocomplete silently inject — they break email
    // matching server-side. (Belt-and-suspenders: backend also sanitizes.)
    const id = identifier
      .normalize('NFKC')
      .replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF\u00A0]/g, '')
      .trim();
    if (!id) {
      setError(t('Please enter your email', 'يرجى إدخال البريد الإلكتروني'));
      setLoading(false);
      return;
    }

    const result = await login(id, password);

    if (result.requires_2fa) {
      navigate('/two-factor');
    } else if (result.success) {
      if (result.user?.deletion_cancelled) {
        try {
          const { toast } = await import('sonner');
          toast.success(t('Your account deletion request was cancelled and your account is active again.',
            'تم إلغاء طلب حذف حسابك وإعادة تفعيله بنجاح.'), { duration: 8000 });
        } catch (_e) { /* non-fatal */ }
      }
      if (result.user?.must_change_password) {
        navigate(result.user?.role === 'Marketing_Partner' ? '/partner/change-password' : '/change-password');
      } else if (result.user?.role === 'Marketing_Partner') {
        navigate('/partner');
      } else if (result.user?.role === 'Parent' && !isParentModuleEnabled()) {
        navigate('/parent-disabled');
      } else {
        navigate('/dashboard');
      }
    } else {
      setErrorType(result.errorType || 'credentials');
      setError(tSafeError(
        result.error,
        t('Login failed. Please try again.', 'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.'),
        language,
      ));
    }
    setLoading(false);
  };

  return (
    <OnboardingShell>
      <style>{`
        @keyframes lgn-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .lgn-anim-rise { animation: lgn-rise 500ms 40ms both ease-out; }
      `}</style>
      <div className="lgn-anim-rise">
      <OnboardingHeader
        title="MESP"
        subtitle={t('Modern Electronic Strategic Platform', 'المنصة الاستراتيجية الإلكترونية الحديثة')}
        icon={BookOpen}
      />

      <OnboardingCard>
        {/* Feb 2026 — the legacy "هل تدرس معنا حالياً؟" hero and its
            "استكمال الانضمام للمنصة" CTA are HIDDEN from the sign-in
            surface pending redesign. The `/legacy-student` route and
            its Workflow are intentionally untouched (deep-link still
            works). See PRD entry: "Login Layout Rebalance (Feb 2026)". */}
        <section dir="rtl" className="text-center" data-testid="login-welcome-header">
          <p
            style={{
              fontSize: 15,
              color: '#C9A24E',
              fontWeight: 700,
              letterSpacing: '0.02em',
              marginBottom: 4,
            }}
          >
            {t('Welcome back', 'أهلاً بعودتك')}
          </p>
          <p
            style={{
              fontSize: 12.5,
              color: 'rgba(255,255,255,0.62)',
              lineHeight: 1.65,
            }}
          >
            {t('Sign in to continue your learning journey', 'سجّل دخولك لمتابعة رحلتك التعليمية')}
          </p>
        </section>

        {/* ── Login form ─────────────────────────────────────────── */}
        {error && (
          <div data-testid="login-error" className="mb-4">
            <OnboardingAlert
              variant={errorType === 'server' || errorType === 'network' ? 'warn' : 'error'}
              title={
                errorType === 'server'
                  ? t('Server is updating', 'الخادم قيد التحديث')
                  : errorType === 'network'
                    ? t('Connection problem', 'تعذّر الاتصال')
                    : errorType === 'locked'
                      ? t('Temporarily locked', 'قفل مؤقت')
                      : t('Login failed', 'فشل تسجيل الدخول')
              }
            >
              {error}
            </OnboardingAlert>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
          dir="rtl"
          autoComplete="on"
          data-testid="login-form"
        >
          {/* Email field */}
          <div className="space-y-1.5">
            <label
              htmlFor="login-email-input"
              className="flex items-center gap-1 text-sm font-medium"
              style={{ color: '#FFFFFF' }}
            >
              {t('Email', 'البريد الإلكتروني')}
              <span style={{ color: '#C9A24E' }}>*</span>
            </label>
            <div className="relative">
              <Mail
                size={16}
                style={{
                  position: 'absolute',
                  right: 'auto',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255,255,255,0.45)',
                  pointerEvents: 'none',
                }}
              />
              <input
                id="login-email-input"
                data-testid="login-email-input"
                type="text"
                inputMode="email"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="name@example.com"
                className="w-full transition-all focus:outline-none"
                dir="ltr"
                style={{
                  background: 'rgba(10, 17, 40, 0.5)',
                  border: '1px solid rgba(201, 162, 78, 0.25)',
                  borderRadius: 12,
                  height: 48,
                  padding: '0 14px 0 42px',
                  color: '#FFFFFF',
                  WebkitTextFillColor: '#FFFFFF',
                  caretColor: '#C9A24E',
                  fontSize: 15,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#C9A24E';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(201, 162, 78, 0.20)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(201, 162, 78, 0.25)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Password field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="login-password-input"
                className="flex items-center gap-1 text-sm font-medium"
                style={{ color: '#FFFFFF' }}
              >
                {t('Password', 'كلمة المرور')}
                <span style={{ color: '#C9A24E' }}>*</span>
              </label>
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="transition-colors"
                style={{ fontSize: 12, color: 'rgba(201,162,78,0.85)', fontWeight: 500 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A24E')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(201,162,78,0.85)')}
              >
                {t('Forgot your password?', 'نسيت كلمة المرور؟')}
              </Link>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t('Hide password', 'إخفاء كلمة المرور') : t('Show password', 'إظهار كلمة المرور')}
                data-testid="toggle-password-visibility"
                className="transition-colors"
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.55)',
                  cursor: 'pointer',
                  padding: 6,
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <Lock
                size={16}
                style={{
                  position: 'absolute',
                  right: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255,255,255,0.45)',
                  pointerEvents: 'none',
                }}
              />
              <input
                id="login-password-input"
                data-testid="login-password-input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Enter your password', 'أدخل كلمة المرور')}
                className="w-full transition-all focus:outline-none"
                dir="rtl"
                style={{
                  background: 'rgba(10, 17, 40, 0.5)',
                  border: '1px solid rgba(201, 162, 78, 0.25)',
                  borderRadius: 12,
                  height: 48,
                  padding: '0 38px 0 38px',
                  color: '#FFFFFF',
                  WebkitTextFillColor: '#FFFFFF',
                  caretColor: '#C9A24E',
                  fontSize: 15,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#C9A24E';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(201, 162, 78, 0.20)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(201, 162, 78, 0.25)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          <GoldButton type="submit" loading={loading} disabled={loading} data-testid="login-submit-button">
            {loading ? (
              <span>{t('Logging in...', 'جاري تسجيل الدخول...')}</span>
            ) : (
              <>
                <LogIn size={18} />
                <span>{t('Login', 'تسجيل الدخول')}</span>
              </>
            )}
          </GoldButton>
        </form>

        <SectionDivider label={t('or', 'أو')} />

        {/* ── 3. Google sign-in ─────────────────────────────────── */}
        <GoogleSignInButton
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS
            // OR REDIRECT URLS — THIS BREAKS THE AUTH.
            // Uses startEmergentGoogleAuth so native Android opens the
            // flow in Chrome instead of the WebView (bypasses Google's
            // ``disallowed_useragent`` block).
            startEmergentGoogleAuth();
          }}
          label={t('Continue with Google', 'المتابعة باستخدام Google')}
          testId="login-google-btn"
        />

        {/* ── Bottom prompt — register CTA only.
             The legacy "تدرس معنا حالياً؟ → انضم للمنصة" secondary
             card is HIDDEN pending redesign (see hero-section note
             above). Route `/legacy-student` still resolves via
             direct URL. */}
        <div
          className="mt-7 pt-5"
          style={{ borderTop: '1px solid rgba(201, 162, 78, 0.18)' }}
          dir="rtl"
          data-testid="login-register-prompt"
        >
          <p className="text-center" style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.72)' }}>
            {t("Don't have an account yet?", 'ليس لديك حساب بعد؟')}{' '}
            <Link
              to="/register"
              data-testid="register-link"
              style={{
                color: '#C9A24E',
                fontWeight: 700,
                textDecoration: 'none',
                borderBottom: '1px dashed rgba(201, 162, 78, 0.45)',
                paddingBottom: 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderBottomColor = '#C9A24E'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderBottomColor = 'rgba(201, 162, 78, 0.45)'; }}
            >
              {t('Apply now', 'قدّم الآن')}
            </Link>
          </p>
        </div>
      </OnboardingCard>

      {/* ── 5. Language switcher pill (under the card) ──────────── */}
      <div className="mt-6 flex items-center justify-center" dir="rtl" data-testid="login-language-switcher">
        <div
          className="inline-flex items-center gap-1 p-1 rounded-full"
          style={{
            background: 'rgba(10, 17, 40, 0.6)',
            border: '1px solid rgba(201, 162, 78, 0.30)',
          }}
        >
          <Globe size={14} style={{ color: 'rgba(255,255,255,0.55)', marginInlineStart: 8 }} />
          <button
            type="button"
            onClick={() => setLanguage('ar')}
            data-testid="login-lang-ar"
            className="transition-all"
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 999,
              background: language === 'ar' ? 'rgba(201, 162, 78, 0.18)' : 'transparent',
              color: language === 'ar' ? '#C9A24E' : 'rgba(255,255,255,0.65)',
              border: language === 'ar' ? '1px solid rgba(201, 162, 78, 0.45)' : '1px solid transparent',
              cursor: 'pointer',
            }}
          >
            العربية
          </button>
          <button
            type="button"
            onClick={() => setLanguage('en')}
            data-testid="login-lang-en"
            className="transition-all"
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 999,
              background: language === 'en' ? 'rgba(201, 162, 78, 0.18)' : 'transparent',
              color: language === 'en' ? '#C9A24E' : 'rgba(255,255,255,0.65)',
              border: language === 'en' ? '1px solid rgba(201, 162, 78, 0.45)' : '1px solid transparent',
              cursor: 'pointer',
            }}
          >
            English
          </button>
        </div>
      </div>

      {/* ── 6. Legal links ──────────────────────────────────────── */}
      <div
        className="mt-4 flex items-center justify-center gap-3 text-xs"
        style={{ color: 'rgba(255,255,255,0.55)' }}
        data-testid="login-legal-links"
      >
        <Link
          to="/privacy"
          data-testid="login-privacy-link"
          className="transition-colors"
          style={{ color: 'rgba(255,255,255,0.65)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A24E')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
        >
          {t('Privacy Policy', 'سياسة الخصوصية')}
        </Link>
        <span>·</span>
        <Link
          to="/terms"
          data-testid="login-terms-link"
          className="transition-colors"
          style={{ color: 'rgba(255,255,255,0.65)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A24E')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
        >
          {t('Terms of Use', 'شروط الاستخدام')}
        </Link>
      </div>
      </div>
    </OnboardingShell>
  );
};

export default Login;
